// FFBB → Arceau : logique pure (aucun appel réseau), testée sur des données
// au format réel de api.ffbb.app (Directus).
//
// « Notre » équipe dans une rencontre = celle dont l'ENGAGEMENT est relié à
// l'équipe Arceau (idEngagementEquipe1 / 2) — jamais une devinette sur le nom
// (le club a plusieurs équipes, « … L'ISLOIS » et « … L'ISLOIS - 2 »).
import { engagementOf } from './annuaire';

export type CompetitionType = 'championnat' | 'coupe' | 'phase_finale' | 'brassage' | 'amical';

const FINALS = /phase\s*final|playoff|play-off|\bfinales?\b|1\/2|1\/4|1\/8|\bdemi|\bquart/;

/** Type d'une compétition d'après son nom (règles de Playbook BCL, validées sur 21 compétitions). */
export function competitionType(name: string): CompetitionType {
  const n = name.toLowerCase();
  if (/brassage|qualif/.test(n)) return 'brassage';
  if (/coupe|troph/.test(n)) return 'coupe';                 // un « Trophée … Finale » reste une coupe
  if (FINALS.test(n)) return 'phase_finale';
  return 'championnat';
}

/**
 * Type d'un engagement d'après la FFBB (D-090) : son `typeCompetition` (DIV, DIV_3x3, COUPE, PLAT —
 * relevé sur 9 480 compétitions le 2026-10-28), précisé par le nom. Une COUPE reste une coupe, même
 * « Qualif. » ou « Finale » ; les AMICAUX (PLAT) sont des amicaux ; « brassage » l'emporte (des DIV
 * « BRASSAGES INITIES PHASE 1 ») ; demi-finales, finales et play-offs (PLAT) sont des phases finales ;
 * un autre plateau (tournoi qualificatif, brassage) est un brassage ; DIV, un championnat (« Phase 2 »
 * comprise). Type inconnu : les règles sur le nom.
 */
export function competitionKind(typeCompetition: string | null | undefined, name: string): CompetitionType {
  const t = (typeCompetition ?? '').trim().toUpperCase(), n = name.toLowerCase();
  if (t === 'COUPE') return 'coupe';
  if (/\bamica/.test(n)) return 'amical';
  if (/brassage/.test(n)) return 'brassage';
  if (FINALS.test(n)) return 'phase_finale';
  if (t === 'PLAT') return 'brassage';
  if (t === 'DIV' || t === 'DIV_3X3') return 'championnat';
  return competitionType(name);
}

export const COMPETITION_LABEL: Record<CompetitionType, string> = {
  championnat: 'Championnat', coupe: 'Coupe', phase_finale: 'Phase finale', brassage: 'Brassage', amical: 'Amical',
};

/** « IE - BASKET ELUSA CLUB - 2 » → « BASKET ELUSA CLUB 2 » (préfixe d'entente et tirets retirés, casse FFBB gardée). */
export function prettyTeamName(name: string | null | undefined): string {
  return (name ?? '').replace(/^\s*(IE|CTC)\s*-\s*/i, '').replace(/\s+-\s+(\d+)\s*$/, ' $1').replace(/\s+/g, ' ').trim();
}

export type EngagementRef = string | number | { id?: string | number | null; idOrganisme?: { id?: string | number | null } | null } | null;

export interface FfbbRencontre {
  id: string | number;
  date_rencontre: string | null;               // 'AAAA-MM-JJTHH:MM:SS', heure locale française
  nomEquipe1: string | null; nomEquipe2: string | null;
  /** L'identifiant, ou l'objet quand la FFBB développe le champ (organisme adverse, annuaire D-094). */
  idEngagementEquipe1: EngagementRef; idEngagementEquipe2: EngagementRef;
  resultatEquipe1?: string | number | null; resultatEquipe2?: string | number | null;
  joue?: boolean | string | null;
  forfaitEquipe1?: boolean | null; forfaitEquipe2?: boolean | null;
  salle?: {
    libelle?: string | null; adresse?: string | null; commune?: { libelle?: string | null } | null;
    cartographie?: { latitude?: number | null; longitude?: number | null; codePostal?: string | null; ville?: string | null } | null;
  } | null;
}

/** Ligne attendue par ffbb_sync_matches(). */
export interface SyncRow {
  ffbb_id: string;
  local_start: string;
  opponent: string;
  is_home: boolean;
  location: string | null;
  score_us: number | null;
  score_them: number | null;
  forfeit: 'us' | 'them' | null;
}

const num = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null);

/** Une rencontre FFBB vue par notre équipe ; null si elle ne nous concerne pas ou n'a pas de date. */
export function toSyncRow(r: FfbbRencontre, engagementId: string): SyncRow | null {
  const e1 = engagementOf(r.idEngagementEquipe1), e2 = engagementOf(r.idEngagementEquipe2);
  if (e1 !== engagementId && e2 !== engagementId) return null;
  if (!r.date_rencontre) return null;
  const home = e1 === engagementId;
  const s1 = num(r.resultatEquipe1), s2 = num(r.resultatEquipe2);
  const played = (r.joue === true || r.joue === 'true') && s1 !== null && s2 !== null;
  const ff1 = !!r.forfaitEquipe1, ff2 = !!r.forfaitEquipe2;
  const salle = r.salle ?? {};
  const location = [salle.libelle, salle.commune?.libelle].filter(Boolean).join(', ') || null;
  return {
    ffbb_id: String(r.id),
    local_start: r.date_rencontre.slice(0, 19),
    opponent: prettyTeamName(home ? r.nomEquipe2 : r.nomEquipe1) || 'Adversaire',
    is_home: home,
    location,
    score_us: played ? (home ? s1 : s2) : null,
    score_them: played ? (home ? s2 : s1) : null,
    forfeit: ff1 || ff2 ? ((home ? ff1 : ff2) ? 'us' : 'them') : null,
  };
}

/** Une rencontre d'une poule, telle que ffbb_poule_apply() l'attend (D-113) : les deux engagements, le
 *  résultat si le match est joué (même règle que toSyncRow), les forfaits. Pas de date, de salle ni de nom :
 *  une lecture de poule ne dépose que des scores, pour tous les clubs Arceau de la poule. */
export interface PouleRow { ffbb_id: string; e1: string; e2: string; s1: number | null; s2: number | null; ff1: boolean; ff2: boolean }

export function pouleRows(raw: FfbbRencontre[]): PouleRow[] {
  return raw.map(r => {
    const s1 = num(r.resultatEquipe1), s2 = num(r.resultatEquipe2);
    const played = (r.joue === true || r.joue === 'true') && s1 !== null && s2 !== null;
    return { ffbb_id: String(r.id), e1: engagementOf(r.idEngagementEquipe1), e2: engagementOf(r.idEngagementEquipe2),
      s1: played ? s1 : null, s2: played ? s2 : null, ff1: !!r.forfaitEquipe1, ff2: !!r.forfaitEquipe2 };
  }).filter(r => /^[0-9A-Za-z_-]{1,40}$/.test(r.ffbb_id));
}

export interface Standing {
  pos: number; team: string; engagementId: string | null; played: number; wins: number; losses: number;
  pf: number; pa: number; diff: number; pts: number; ff: number;
}

/** Classement officiel d'une poule (items/ffbbserver_poules/<id>, champ classements). */
export function standingsFrom(classements: Record<string, unknown>[] | null | undefined): Standing[] {
  const N = (v: unknown) => num(v) ?? 0;
  return (classements ?? [])
    .filter(c => !c.horsClassement)
    .map(c => ({
      pos: N(c.position), team: prettyTeamName(String(c.organisme_nom ?? '')),
      engagementId: c.idEngagement != null ? String(c.idEngagement) : null,
      played: N(c.matchJoues), wins: N(c.gagnes), losses: N(c.perdus), ff: N(c.nombreForfaits),
      pf: N(c.paniersMarques), pa: N(c.paniersEncaisses),
      diff: c.difference !== null && c.difference !== undefined && c.difference !== '' ? N(c.difference) : N(c.paniersMarques) - N(c.paniersEncaisses),
      pts: N(c.points),
    }))
    .sort((a, b) => a.pos - b.pos || b.pts - a.pts);
}

/** Notre ligne dans un classement : par l'engagement (le nom diffère : « … L'ISLOIS » / « … L'ISLOIS - 1 »). */
export function ourStanding(rows: Standing[], engagementId: string | null | undefined): Standing | null {
  return engagementId ? rows.find(r => r.engagementId === engagementId) ?? null : null;
}
